import { collection, addDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AIAssistantQuery } from '../types';
import { GOOGLE_GENAI_API_KEY, isGenAIConfigured, defaultGenAIConfig } from '../config/genai';

const COLLECTION_NAME = 'ai_assistant_queries';

// Function to save a query and response to Firestore
export const saveAIQuery = async (userId: string, query: string, response: string): Promise<AIAssistantQuery> => {
  try {
    const queryData = {
      userId,
      query,
      response,
      createdAt: new Date()
    };
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), queryData);
    return { id: docRef.id, ...queryData };
  } catch (error) {
    console.error('Error saving AI query:', error);
    throw error;
  }
};

// Function to get recent queries for a user
export const getRecentQueries = async (userId: string, queryLimit = 10): Promise<AIAssistantQuery[]> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(queryLimit)
    );
    
    const querySnapshot = await getDocs(q);
    const queries: AIAssistantQuery[] = [];
    
    querySnapshot.forEach((doc) => {
      queries.push({ id: doc.id, ...doc.data() } as AIAssistantQuery);
    });
    
    return queries;
  } catch (error) {
    console.error('Error getting recent queries:', error);
    throw error;
  }
};

// Function to send a query to Google Generative AI
export const sendQueryToGenAI = async (prompt: string): Promise<string> => {
  if (!isGenAIConfigured()) {
    throw new Error('Google Generative AI API key is not configured');
  }
  
  try {
    // This is a placeholder for the actual API call
    // In a real implementation, you would use the Google Generative AI SDK or API
    const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${GOOGLE_GENAI_API_KEY}`
      },
      body: JSON.stringify({
        contents: [
          {
            parts: [
              {
                text: prompt
              }
            ]
          }
        ],
        generationConfig: {
          temperature: defaultGenAIConfig.temperature,
          topK: defaultGenAIConfig.topK,
          topP: defaultGenAIConfig.topP,
          maxOutputTokens: defaultGenAIConfig.maxOutputTokens,
        }
      })
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    return data.candidates[0].content.parts[0].text;
  } catch (error) {
    console.error('Error sending query to GenAI:', error);
    throw error;
  }
};

// Function to process a tax-related query
export const processTaxQuery = async (userId: string, query: string): Promise<string> => {
  try {
    // Enhance the prompt with tax-specific context
    const enhancedPrompt = `
      As a tax assistant, please help with the following query:
      
      ${query}
      
      Please provide accurate information based on US tax laws and regulations.
      If you're unsure about any specific details, please indicate that.
    `;
    
    // Send the enhanced prompt to the AI
    const response = await sendQueryToGenAI(enhancedPrompt);
    
    // Save the query and response to Firestore
    await saveAIQuery(userId, query, response);
    
    return response;
  } catch (error) {
    console.error('Error processing tax query:', error);
    if (error instanceof Error) {
      return `Sorry, I couldn't process your query. Error: ${error.message}`;
    }
    return 'Sorry, I couldn\'t process your query. An unknown error occurred.';
  }
}; 